<template>
  <div class="viq-market-entry">
    <div class="viq-market-entry__header">
      <h3 class="viq-market-entry__title">Market Entry Assessment</h3>
      <el-button v-if="step === 'form'" size="small" @click="goBack">Back</el-button>
    </div>

    <!-- Form step -->
    <div v-if="step === 'form'" class="viq-market-entry__form">
      <p class="viq-market-entry__desc">
        Get a comprehensive regulatory landscape assessment for entering a new jurisdiction.
      </p>

      <div class="viq-market-entry__field">
        <label>Target Jurisdiction</label>
        <el-input v-model="jurisdiction" placeholder="e.g., Germany, United Kingdom, Singapore..." />
      </div>

      <div class="viq-market-entry__field">
        <label>Business Type</label>
        <el-input v-model="businessType" placeholder="e.g., Payment institution, E-money provider..." />
      </div>

      <div class="viq-market-entry__field">
        <label>Assessment Depth</label>
        <div class="viq-market-entry__levels">
          <div
            v-for="level in levels"
            :key="level.id"
            class="viq-market-entry__level"
            :class="{ 'viq-market-entry__level--active': selectedLevel === level.id }"
            @click="selectedLevel = level.id"
          >
            <h4>{{ level.title }}</h4>
            <p>{{ level.description }}</p>
          </div>
        </div>
      </div>

      <div class="viq-market-entry__form-actions">
        <el-button
          type="primary"
          :disabled="!canSubmit"
          @click="generate"
        >
          Generate Assessment
        </el-button>
      </div>
    </div>

    <!-- Results step -->
    <div v-else-if="step === 'results'" class="viq-market-entry__results">
      <div v-if="isStreaming" class="viq-market-entry__streaming">
        <span class="viq-market-entry__spinner" />
        Generating market entry assessment...
      </div>
      <div
        v-if="streamingText || reportContent"
        class="viq-market-entry__content"
        v-html="renderedContent"
      />
      <div v-if="!isStreaming" class="viq-market-entry__actions">
        <el-button type="primary" @click="$emit('export-pdf')">Export as PDF</el-button>
        <el-button v-if="$config.features && $config.features.viqShare" @click="share">Save &amp; Share</el-button>
        <el-button @click="reset">New Assessment</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ViqMarketEntry",
  data() {
    return {
      step: "form",
      jurisdiction: "",
      businessType: "",
      selectedLevel: "discovery",
      reportContent: "",
      levels: [
        {
          id: "discovery",
          title: "Discovery",
          description: "High-level overview of regulatory landscape, key requirements, and timeline estimates.",
        },
        {
          id: "operational",
          title: "Operational",
          description: "Detailed licensing requirements, compliance obligations, and implementation roadmap.",
        },
        {
          id: "comprehensive",
          title: "Comprehensive",
          description: "Full regulatory assessment with risk analysis, cost estimates, and strategic recommendations.",
        },
      ],
    };
  },
  computed: {
    ...mapState("Viq", ["isStreaming", "streamingText"]),
    canSubmit() {
      return this.jurisdiction.trim() && this.selectedLevel;
    },
    renderedContent() {
      const text = this.reportContent || this.streamingText || "";
      if (!text) return "";
      let html = text;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
  watch: {
    isStreaming(val, old) {
      if (old && !val && this.step === "results") {
        this.reportContent = this.streamingText;
      }
    },
  },
  methods: {
    goBack() {
      this.$store.commit("Viq/SET_REPORT_STEP", 0);
      this.$store.commit("Viq/SET_REPORT_TYPE", null);
    },
    generate() {
      this.step = "results";
      this.$store.dispatch("Viq/streamReport", {
        conversationId: null,
        sections: [
          {
            title: "Market Entry Assessment",
            topic: `${this.businessType} market entry in ${this.jurisdiction}`,
            level: this.selectedLevel,
            type: "market-entry",
          },
        ],
      });
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "market-entry",
        content: this.reportContent,
      });
    },
    reset() {
      this.step = "form";
      this.jurisdiction = "";
      this.businessType = "";
      this.selectedLevel = "discovery";
      this.reportContent = "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-market-entry {
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
  padding: $viq-space-lg 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $viq-space-lg;
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__desc {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
    line-height: 1.5;
  }

  &__form {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
  }

  &__field {
    margin-bottom: $viq-space-md;

    label {
      display: block;
      font-size: $viq-font-sm;
      font-weight: 600;
      color: $viq-text-primary;
      margin-bottom: $viq-space-sm;
    }
  }

  &__levels {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: $viq-space-sm;
  }

  &__level {
    padding: $viq-space-md;
    border: 2px solid $viq-border;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: border-color $viq-transition-fast;
    text-align: center;

    &:hover {
      border-color: $viq-accent;
    }

    &--active {
      border-color: $viq-accent;
      background: $viq-citation-bg;
    }

    h4 {
      font-size: $viq-font-sm;
      font-weight: 700;
      color: $viq-text-primary;
      margin: 0 0 $viq-space-xs;
    }

    p {
      font-size: $viq-font-xs;
      color: $viq-text-secondary;
      margin: 0;
      line-height: 1.4;
    }
  }

  &__form-actions {
    margin-top: $viq-space-lg;
    display: flex;
    justify-content: flex-end;
  }

  &__streaming {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-md;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    ::v-deep {
      h2, h3 { margin: $viq-space-lg 0 $viq-space-sm; font-weight: 700; }
      p { margin: 0 0 $viq-space-sm; }
      ul { padding-left: $viq-space-lg; margin: $viq-space-sm 0; }
      li { margin-bottom: $viq-space-xs; }
      strong { font-weight: 700; }
    }
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
    margin-top: $viq-space-lg;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
